// @ts-nocheck
import React, { useState, useEffect } from 'react';

/**
 * 🏢 Στοιχεία Εταιρείας Ι.Κ.Ε. — settings panel (Λογιστικά → Ρυθμίσεις tab).
 * Loads/saves company_info via /api/company-info.php (GET/PUT).
 * Self-contained: own state + styles, no dependency on the Παραστατικά form.
 */

const EMPTY = {
  name: '', vat: '', doy: '', profession: '', address: '', city: '', postal_code: '',
  name_en: '', profession_en: '', doy_en: '', address_en: '', city_en: '', postal_code_en: '',
  email_accounting: '', email_charter: '', email_info: '', phone: '', gemi: '', iban: '',
};

const inputStyle: React.CSSProperties = {
  width: '100%', padding: '8px 10px', border: '1px solid #cbd5e0',
  borderRadius: 6, fontSize: 14, boxSizing: 'border-box',
};
const labelStyle: React.CSSProperties = { display: 'block', fontSize: 12, fontWeight: 600, color: '#4a5568', marginBottom: 4 };

const Field: React.FC<{ label: string; children: React.ReactNode }> = ({ label, children }) => (
  <div style={{ marginBottom: 12 }}>
    <label style={labelStyle}>{label}</label>
    {children}
  </div>
);

const CompanyInfoSettings: React.FC = () => {
  const [company, setCompany] = useState({ ...EMPTY });
  const [saving, setSaving] = useState(false);
  const setField = (k: string, v: string) => setCompany(prev => ({ ...prev, [k]: v }));

  const applyRow = (row: any) => setCompany(prev => {
    const next = { ...prev };
    Object.keys(EMPTY).forEach(k => { next[k] = row[k] ?? ''; });
    return next;
  });

  useEffect(() => {
    fetch('./api/company-info.php')
      .then(r => r.json())
      .then(d => { if (d && d.success && d.company) applyRow(d.company); })
      .catch(err => console.error('company-info load failed', err));
  }, []);

  const save = async () => {
    if (!company.name.trim() || !company.vat.trim()) {
      alert('Επωνυμία και ΑΦΜ είναι υποχρεωτικά.');
      return;
    }
    setSaving(true);
    try {
      const r = await fetch('./api/company-info.php', {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(company),
      });
      const d = await r.json();
      if (d && d.success) {
        if (d.company) applyRow(d.company);
        alert('✅ Τα στοιχεία της Ι.Κ.Ε. αποθηκεύτηκαν.');
      } else {
        alert('❌ Σφάλμα αποθήκευσης: ' + (d?.error || 'unknown'));
      }
    } catch (err) {
      console.error('company-info save failed', err);
      alert('❌ Σφάλμα σύνδεσης κατά την αποθήκευση.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div style={{ maxWidth: 640 }}>
      <h2 style={{ marginTop: 0 }}>🏢 Στοιχεία Εταιρείας Ι.Κ.Ε.</h2>

      {/* Επωνυμία GR / EN */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
        <Field label="Επωνυμία (GR) *"><input value={company.name} onChange={e => setField('name', e.target.value)} style={inputStyle} /></Field>
        <Field label="Company (EN)"><input value={company.name_en} onChange={e => setField('name_en', e.target.value)} style={inputStyle} /></Field>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
        <Field label="ΑΦΜ *"><input value={company.vat} onChange={e => setField('vat', e.target.value)} style={inputStyle} /></Field>
        <div />
      </div>

      {/* Επάγγελμα GR / EN */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
        <Field label="Επάγγελμα (GR)"><input value={company.profession} onChange={e => setField('profession', e.target.value)} style={inputStyle} /></Field>
        <Field label="Profession (EN)"><input value={company.profession_en} onChange={e => setField('profession_en', e.target.value)} style={inputStyle} /></Field>
      </div>

      {/* ΔΟΥ GR / EN */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
        <Field label="ΔΟΥ (GR)"><input value={company.doy} onChange={e => setField('doy', e.target.value)} style={inputStyle} /></Field>
        <Field label="Tax Office (EN)"><input value={company.doy_en} onChange={e => setField('doy_en', e.target.value)} style={inputStyle} /></Field>
      </div>

      {/* Διεύθυνση GR / EN */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
        <Field label="Διεύθυνση (GR)"><input value={company.address} onChange={e => setField('address', e.target.value)} style={inputStyle} /></Field>
        <Field label="Address (EN)"><input value={company.address_en} onChange={e => setField('address_en', e.target.value)} style={inputStyle} /></Field>
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
        <Field label="Πόλη (GR)"><input value={company.city} onChange={e => setField('city', e.target.value)} style={inputStyle} /></Field>
        <Field label="City (EN)"><input value={company.city_en} onChange={e => setField('city_en', e.target.value)} style={inputStyle} /></Field>
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
        <Field label="Τ.Κ. (GR)"><input value={company.postal_code} onChange={e => setField('postal_code', e.target.value)} style={inputStyle} /></Field>
        <Field label="Postal Code (EN)"><input value={company.postal_code_en} onChange={e => setField('postal_code_en', e.target.value)} style={inputStyle} /></Field>
      </div>

      <Field label="Email Λογιστικού"><input value={company.email_accounting} onChange={e => setField('email_accounting', e.target.value)} style={inputStyle} /></Field>
      <Field label="Email Ναυλώσεων"><input value={company.email_charter} onChange={e => setField('email_charter', e.target.value)} style={inputStyle} /></Field>
      <Field label="Email Γενικής Επικοινωνίας"><input value={company.email_info} onChange={e => setField('email_info', e.target.value)} style={inputStyle} /></Field>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
        <Field label="Τηλέφωνο"><input value={company.phone} onChange={e => setField('phone', e.target.value)} style={inputStyle} /></Field>
        <Field label="ΓΕΜΗ"><input value={company.gemi} onChange={e => setField('gemi', e.target.value)} style={inputStyle} /></Field>
      </div>
      <Field label="IBAN"><input value={company.iban} onChange={e => setField('iban', e.target.value)} style={inputStyle} /></Field>
      <button
        onClick={save}
        disabled={saving}
        style={{
          width: '100%', marginTop: 10, padding: '10px 18px', borderRadius: 6, border: 'none',
          cursor: saving ? 'default' : 'pointer', fontSize: 14, fontWeight: 600,
          background: '#7c3aed', color: '#fff', opacity: saving ? 0.6 : 1,
        }}
      >
        {saving ? '...' : '💾 Αποθήκευση Στοιχείων Ι.Κ.Ε.'}
      </button>
    </div>
  );
};

export default CompanyInfoSettings;
